import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  clientBalances,
  clientName,
  formatPrice,
  tagColorMap,
  zlotyToGrosz,
  type Client,
  type ClientInput,
} from '@diana/shared'
import { api } from '../../lib/api.js'
import { ColorDot } from '../../components/ColorPicker.js'
import { Column, Main, TopTitle } from '../../classroom/Shell.js'
import { Button, ErrorLine, SearchBox, Skeleton, Tabs } from '../../classroom/ui.js'
import { ClientForm } from './ClientForm.js'
import { TagsManager } from './TagsManager.js'
import { Standing, lastPackage, packageLeft, td, th } from './books.js'

/** 'active' | 'inactive' | 'tag:<name>' — and 'tags' for the colours view. */
type View = string

/**
 * Studio → Uczniowie (Studio-Clients.png): Diana's students — who they are,
 * what they pay, where they stand. Click a name for the full card.
 *
 * The tabs double as the tag filter: tag someone "premium" or "low" and the
 * list (and the books) can be sliced the same way. „Tagi i kolory” swaps the
 * list for the tag painter. Start's search box sends people here with `?q=`.
 */
export function ClientsPage() {
  const queryClient = useQueryClient()
  const [params, setParams] = useSearchParams()
  const clientsQuery = useQuery({ queryKey: ['studio', 'clients'], queryFn: api.studio.clients })
  const tagsQuery = useQuery({ queryKey: ['studio', 'tags'], queryFn: api.studio.tags })
  const books = useQuery({ queryKey: ['studio', 'accounting'], queryFn: api.studio.accounting })

  const [query, setQuery] = useState(() => params.get('q') ?? '')
  const [view, setView] = useState<View>('active')
  const [adding, setAdding] = useState(false)
  const [added, setAdded] = useState<Client | null>(null)

  const create = useMutation({
    mutationFn: (input: ClientInput) => api.studio.createClient(input),
    onSuccess: (client) => {
      queryClient.invalidateQueries({ queryKey: ['studio', 'clients'] })
      queryClient.invalidateQueries({ queryKey: ['studio', 'accounting'] })
      setAdding(false)
      setAdded(client)
    },
  })

  const search = (v: string) => {
    setQuery(v)
    setParams(
      (p) => {
        const n = new URLSearchParams(p)
        if (v.trim()) n.set('q', v.trim())
        else n.delete('q')
        return n
      },
      { replace: true },
    )
  }

  const clients = clientsQuery.data ?? []
  const tagRows = tagsQuery.data ?? []
  const tagColors = useMemo(() => tagColorMap(tagRows), [tagRows])
  const allTags = useMemo(() => [...new Set(clients.flatMap((c) => c.tags))].sort(), [clients])

  const balances = useMemo(() => {
    if (!books.data) return new Map<string, ReturnType<typeof clientBalances>[number]>()
    const rows = clientBalances(books.data.clients, books.data.lessons, books.data.purchases, books.data.today)
    return new Map(rows.map((r) => [r.clientId, r]))
  }, [books.data])

  /** The first lesson still to come, for „nowy · lekcja 21.10”. */
  const nextLesson = useMemo(() => {
    const out = new Map<string, string>()
    if (!books.data) return out
    for (const l of books.data.lessons) {
      if (!l.clientId || l.day < books.data.today || (l.status !== 'confirmed' && l.status !== 'pending')) continue
      const seen = out.get(l.clientId)
      if (!seen || l.day < seen) out.set(l.clientId, l.day)
    }
    return out
  }, [books.data])

  const matches = (c: Client) => {
    const q = query.trim().toLowerCase()
    if (!q) return true
    return `${c.firstName} ${c.lastName} ${c.email} ${c.phone}`.toLowerCase().includes(q)
  }
  const inView = (c: Client, v: View) =>
    v === 'inactive' ? !c.active : c.active && (!v.startsWith('tag:') || c.tags.includes(v.slice(4)))

  const visible = clients.filter((c) => inView(c, view) && matches(c))
  /** Nobody here, but someone in the other half of the list matches. */
  const elsewhere = visible.length === 0 && query.trim() ? clients.find((c) => matches(c)) : undefined
  const activeCount = clients.filter((c) => c.active).length

  const toggleAdd = () => {
    setAdding((v) => !v)
    setAdded(null)
    create.reset()
  }

  return (
    <Column
      density="studio"
      top={
        <>
          <TopTitle studio>Uczniowie</TopTitle>
          <SearchBox
            className="ml-5"
            width={280}
            value={query}
            onChange={search}
            placeholder="Szukaj ucznia…"
            label="Szukaj ucznia"
          />
          <Button
            variant={adding ? 'secondary' : 'primary'}
            onClick={toggleAdd}
            className="ml-auto min-h-10 px-5 text-class-gloss"
          >
            {adding ? 'Anuluj' : '+ Dodaj ucznia'}
          </Button>
        </>
      }
    >
      <Main density="studio" className="!gap-5">
        {/* Phone: the top bar is hidden, so its search and button come here. */}
        <div className="flex flex-col gap-3 desk:hidden">
          <SearchBox width={600} value={query} onChange={search} placeholder="Szukaj ucznia…" label="Szukaj ucznia na liście" />
          <Button variant={adding ? 'secondary' : 'primary'} onClick={toggleAdd} className="min-h-10 text-class-gloss">
            {adding ? 'Anuluj' : '+ Dodaj ucznia'}
          </Button>
        </div>

        {adding && (
          <section className="cl-card mb-5 flex flex-col gap-5 p-6 desk:p-8">
            <h2 className="cl-subheading m-0">Nowy uczeń</h2>
            <ClientForm
              tagColors={tagColors}
              onSubmit={(input) => create.mutate(input)}
              busy={create.isPending}
              error={create.isError ? 'Nie udało się dodać. Sprawdź dane.' : ''}
              submitLabel="Dodaj ucznia"
              onCancel={toggleAdd}
            />
          </section>
        )}

        {added && (
          <p role="status" className="m-0 font-semibold text-class-correct">
            ✓ Dodano:{' '}
            <Link to={`/studio/klienci/${added.id}`} className="font-semibold">
              {clientName(added)} ›
            </Link>
          </p>
        )}

        <div className="flex flex-wrap items-end gap-x-6 gap-y-2 border-b border-class-line">
          <Tabs
            label="Uczniowie"
            value={view}
            onChange={setView}
            items={[
              { value: 'active', label: `Aktywni · ${activeCount}` },
              ...allTags.map((t) => ({ value: `tag:${t}`, label: t })),
              { value: 'inactive', label: 'Nieaktywni' },
            ]}
          />
          <button
            type="button"
            aria-pressed={view === 'tags'}
            onClick={() => setView(view === 'tags' ? 'active' : 'tags')}
            className={[
              'ml-auto cursor-pointer border-0 border-b-2 bg-transparent px-0 pb-1.5 font-class-text text-class-gloss font-semibold',
              view === 'tags' ? 'border-class-sky-700 text-class-ink' : 'border-transparent text-class-sky-700 hover:text-class-ink',
            ].join(' ')}
          >
            Tagi i kolory
          </button>
        </div>

        {view === 'tags' ? (
          tagsQuery.isPending ? (
            <Skeleton />
          ) : tagsQuery.isError ? (
            <ErrorLine>Nie udało się wczytać tagów. Odśwież stronę.</ErrorLine>
          ) : (
            <TagsManager tags={tagRows} />
          )
        ) : clientsQuery.isPending ? (
          <Skeleton lines={5} />
        ) : clientsQuery.isError ? (
          <ErrorLine>Nie udało się wczytać uczniów. Odśwież stronę.</ErrorLine>
        ) : visible.length === 0 ? (
          <p className="m-0 py-4 text-class-ink-soft">
            {clients.length === 0
              ? 'Jeszcze nie masz żadnego ucznia.'
              : elsewhere
                ? (
                    <>
                      W tej zakładce nikt nie pasuje.{' '}
                      <button
                        type="button"
                        onClick={() => setView(elsewhere.active ? 'active' : 'inactive')}
                        className="cl-link"
                      >
                        Pokaż w zakładce {elsewhere.active ? 'Aktywni' : 'Nieaktywni'} ›
                      </button>
                    </>
                  )
                : 'Nikt nie pasuje do filtra.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-left text-class-gloss">
              <thead>
                <tr>
                  <th className={th}>Uczeń</th>
                  <th className={th}>Tag</th>
                  <th className={th}>Stawka</th>
                  <th className={th}>W pakiecie</th>
                  <th className={th}>Saldo</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => {
                  const b = balances.get(c.id)
                  const pkg = books.data ? lastPackage(c.id, books.data.purchases) : undefined
                  return (
                    <tr key={c.id}>
                      <td className={td}>
                        <Link to={`/studio/klienci/${c.id}`} className="block leading-snug">
                          <span className="block font-semibold">{clientName(c)}</span>
                          <span className="block text-class-label text-class-ink-faint">
                            {[c.phone, c.email].filter(Boolean).join(' · ') || '—'}
                          </span>
                        </Link>
                      </td>
                      <td className={`${td} text-class-ink-soft`}>
                        {c.tags.length === 0 ? (
                          <span className="text-class-ink-faint">—</span>
                        ) : (
                          <span className="flex flex-wrap gap-x-3 gap-y-1">
                            {c.tags.map((t) => (
                              <span key={t} className="inline-flex items-center gap-2 whitespace-nowrap">
                                {tagColors.has(t) && <ColorDot color={tagColors.get(t)!} size={7} />}
                                {t}
                              </span>
                            ))}
                          </span>
                        )}
                      </td>
                      <td className={`${td} whitespace-nowrap text-class-ink-soft`}>
                        {c.defaultPriceGrosz ? formatPrice(c.defaultPriceGrosz) : '—'}
                      </td>
                      <td className={`${td} whitespace-nowrap`}>{books.data ? packageLeft(b, pkg, true) : ''}</td>
                      <td className={td}>
                        {books.data && <Standing b={b} pkg={pkg} usedUp="pakiet wykorzystany" nextLesson={nextLesson.get(c.id)} />}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {view !== 'tags' && clients.length > 0 && (
          <p className="m-0 mt-2 text-class-gloss text-class-ink-faint">
            Kolor tagu maluje lekcje ucznia w kalendarzu. Kolejność tagów ma znaczenie — liczy się pierwszy kolorowy.
          </p>
        )}
      </Main>
    </Column>
  )
}

/**
 * A money balance as a number: negative = they owe (red, with a minus),
 * positive = prepaid. Used on the client card.
 */
export function Balance({ grosz }: { grosz: number }) {
  if (grosz === 0) return <span className="text-class-ink-faint">0 zł</span>
  return (
    <span className={grosz < 0 ? 'text-class-wrong' : 'text-class-correct'}>
      {grosz < 0 ? `−${formatPrice(-grosz)}` : `+${formatPrice(grosz)}`}
    </span>
  )
}

export { zlotyToGrosz }
